import React, { useState } from "react";
import PropTypes from "prop-types";
import DayPicker, { DateUtils } from "react-day-picker";
import { Popover, PopoverBody } from "reactstrap";
import "react-day-picker/lib/style.css";
import "./date-range-picker.scss";

const propTypes = {
  nameFrom: PropTypes.string.isRequired,
  nameTo: PropTypes.string.isRequired,
  defaultFrom: PropTypes.string,
  defaultTo: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onApply: PropTypes.func
};

const DateRangePicker = ({
  nameFrom,
  nameTo,
  defaultFrom = "",
  defaultTo = "",
  minDate = "",
  maxDate = "",
  placeholder = "Select date range",
  className = "",
  disabled = false,
  onApply = () => {}
}) => {
  // Parse date string as local date (not UTC) to avoid timezone issues
  const parseLocalDate = dateString => {
    if (!dateString) return null;
    // Parse as YYYY-MM-DD and create date in local timezone
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const [from, setFrom] = useState(parseLocalDate(defaultFrom));
  const [to, setTo] = useState(parseLocalDate(defaultTo));
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const formatDate = date => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatDateForInput = date => {
    if (!date) return "";
    // Format as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDayClick = day => {
    const range = DateUtils.addDayToRange(day, { from, to });
    setFrom(range.from);
    setTo(range.to);
  };

  const togglePopover = () => {
    setIsOpen(!isOpen);
  };

  const navigateMonth = direction => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDisplayText = () => {
    if (!from && !to) return placeholder;
    if (from && to) return `${formatDate(from)} - ${formatDate(to)}`;
    if (from) return `From ${formatDate(from)}`;
    return placeholder;
  };

  const modifiers = { start: from, end: to };
  const disabledDays = [
    { before: parseLocalDate(minDate) || new Date(0) },
    { after: parseLocalDate(maxDate) || new Date() }
  ];
  const selectedDays = [from, { from, to }];

  return (
    <div className={`date-range-picker ${className}`}>
      <div className="position-relative">
        <input
          type="text"
          className="form-control"
          value={getDisplayText()}
          placeholder={placeholder}
          onClick={togglePopover}
          readOnly
          disabled={disabled}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: "white"
          }}
          id={`${nameFrom}-${nameTo}-picker`}
        />

        {/* Hidden inputs for form submission */}
        <input type="hidden" name={nameFrom} value={formatDateForInput(from)} />
        <input type="hidden" name={nameTo} value={formatDateForInput(to)} />

        <Popover
          placement="bottom-start"
          isOpen={isOpen}
          target={`${nameFrom}-${nameTo}-picker`}
          toggle={togglePopover}
          className="date-range-picker__popover"
        >
          <PopoverBody className="date-range-picker__popover-body">
            <div className="date-range-picker__header">
              <button
                type="button"
                className="date-range-picker__nav-button"
                onClick={() => navigateMonth(-1)}
              >
                ‹
              </button>
              <div className="date-range-picker__month-year">
                {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </div>
              <button
                type="button"
                className="date-range-picker__nav-button"
                onClick={() => navigateMonth(1)}
              >
                ›
              </button>
            </div>

            <DayPicker
              className="date-range-picker__calendar"
              month={currentMonth}
              selectedDays={selectedDays}
              modifiers={modifiers}
              onDayClick={handleDayClick}
              disabledDays={disabledDays}
              showOutsideDays={false}
              onMonthChange={setCurrentMonth}
              captionElement={() => null}
              navbarElement={() => null}
            />

            <div className="date-range-picker__actions">
              <button
                type="button"
                className="date-range-picker__button date-range-picker__button--cancel"
                onClick={() => {
                  setFrom(null);
                  setTo(null);
                  setIsOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="date-range-picker__button date-range-picker__button--apply"
                onClick={() => {
                  setIsOpen(false);
                  onApply(from, to);
                }}
              >
                Apply
              </button>
            </div>
          </PopoverBody>
        </Popover>
      </div>
    </div>
  );
};

DateRangePicker.propTypes = propTypes;

export default DateRangePicker;
